import { useState } from 'react';
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useDataDomain } from '../../graphql/queries';
import { useApolloClient } from '@apollo/client/react';
import { DataDomainForm } from '../../components/information';
import { updateDataDomain, ApiError, type UpdateDataDomainInput } from '../../lib/apiClient';
import { LoadingSpinner, EmptyState } from '../../components/shared';
import type { CreateDataDomainFormData } from '../../components/forms/validation';
import '@tylertech/forge/icon';
import '@tylertech/forge/banner';
import './$dataDomainId.edit.css';

export const Route = createFileRoute('/information/$dataDomainId/edit')({
  component: EditDataDomainPage,
});

export { EditDataDomainPage };

function EditDataDomainPage(): JSX.Element {
  const { dataDomainId } = Route.useParams();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data, loading, error: fetchError } = useDataDomain(dataDomainId);
  const domain = data?.dataDomain;

  const IconElement = 'forge-icon' as React.ElementType;
  const BannerElement = 'forge-banner' as React.ElementType;

  const handleSubmit = async (formData: CreateDataDomainFormData): Promise<void> => {
    setIsSubmitting(true);
    setError(null);
    try {
      /* c8 ignore next 16 */
      const input: UpdateDataDomainInput = {
        name: formData.name,
        description: formData.description || undefined,
        domainType: formData.domainType,
        dataStewardId: formData.dataStewardId,
        owningBusinessUnitId: formData.owningBusinessUnitId,
        technicalOwnerId: formData.technicalOwnerId || undefined,
        defaultClassification: formData.defaultClassification,
        containsPII: formData.containsPII ?? false,
        containsPCI: formData.containsPCI ?? false,
        containsPHI: formData.containsPHI ?? false,
        lifecycleStatus: formData.lifecycleStatus || undefined,
        criticalityLevel: formData.criticalityLevel,
        notes: formData.notes || undefined,
        documentationUrl: formData.documentationUrl || undefined,
      };
      await updateDataDomain(dataDomainId, input);
      await apolloClient.refetchQueries({
        include: ['GetDataDomain', 'ListDataDomains'],
      });
      void navigate({
        to: '/information/$dataDomainId',
        params: { dataDomainId },
      });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    void navigate({
      to: '/information/$dataDomainId',
      params: { dataDomainId },
    });
  };

  if (loading) {
    return (
      <div className="edit-data-domain-page">
        <LoadingSpinner centered message="Loading data domain..." />
      </div>
    );
  }

  if (fetchError || !domain) {
    return (
      <div className="edit-data-domain-page">
        <EmptyState
          icon="error"
          title="Failed to load data domain"
          description={fetchError?.message || 'Data domain not found'}
          actionLabel="Back to Data Domains"
          /* c8 ignore next */
          onAction={() => void navigate({ to: '/information', search: { page: 1, pageSize: 25 } })}
        />
      </div>
    );
  }

  const defaultValues: Partial<CreateDataDomainFormData> = {
    name: domain.name,
    description: domain.description || undefined,
    domainType: domain.domainType,
    dataStewardId: domain.dataStewardId,
    owningBusinessUnitId: domain.owningBusinessUnitId,
    technicalOwnerId: domain.technicalOwnerId || undefined,
    defaultClassification: domain.defaultClassification,
    containsPII: domain.containsPII,
    containsPCI: domain.containsPCI,
    containsPHI: domain.containsPHI,
    lifecycleStatus: domain.lifecycleStatus,
    criticalityLevel: domain.criticalityLevel,
    notes: domain.notes || undefined,
    documentationUrl: domain.documentationUrl || undefined,
  };

  return (
    <div className="edit-data-domain-page">
      <div className="breadcrumb">
        <Link to="/information/$dataDomainId" params={{ dataDomainId }} className="breadcrumb-link">
          <IconElement name="arrow_back" aria-hidden="true" />
          <span>Back to {domain.name}</span>
        </Link>
      </div>

      <div className="page-header page-header--stacked">
        <h1 className="forge-typography--headline4">Edit Data Domain</h1>
        <p className="forge-typography--body2 page-header-subtitle">
          Update the details for {domain.name}
        </p>
      </div>

      {error && (
        <BannerElement theme="error" dismissed={false} className="error-banner" role="alert">
          <div className="banner-content">
            <IconElement name="error" aria-hidden="true" />
            <span>{error}</span>
          </div>
        </BannerElement>
      )}

      <DataDomainForm
        mode="edit"
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
